import { APP_BASE_PATH, getSelfUrlOrigin, pathForNextNavigation, pathWithAppBase } from '@/lib/appBasePath';

export const BLYN_HOST_BASE = '/ai-agent';

/** If CloudFront served `/v2/index.html` for a Blyn URL, recover the real host path. */
export function recoverBlynPathFromWindow(pathname: string, search = ''): string | null {
  const path = pathForNextNavigation(pathname);
  if (path === BLYN_HOST_BASE || path.startsWith(`${BLYN_HOST_BASE}/`)) {
    return `${path}${search}`;
  }
  return null;
}

/** Blyn views that must exist as static HTML so CloudFront's `uri + '.html'` rewrite hits a real file. */
export const BLYN_HOST_VIEWS = [
  'conversation',
  'new-conversation',
  'projects',
  'project-workspace',
  'tasks',
  'staged',
  'approvals',
  'creatives',
  'knowledge',
  'forecast',
  'nudges',
  'notifications',
  'web-personalization',
  'product-catalog',
  'track-record',
  'audit-log',
  'settings',
  'compose',
  'first-run',
  'help',
  'search',
] as const;

/**
 * Read Blyn's internal route from the live browser URL.
 * Static export + CloudFront rewrite serve a placeholder `_.html`, so
 * `usePathname()` / `useParams()` cannot be trusted for the real slug.
 *
 * `/v2/ai-agent/projects?id=1` → `/blyn/projects?id=1`
 * `/v2/ai-agent` or `/v2/ai-agent/_` → undefined (let Blyn pick the default)
 */
export function readBlynRouteFromWindow(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const rawPath = window.location.pathname;
  const path = rawPath.startsWith(APP_BASE_PATH)
    ? rawPath.slice(APP_BASE_PATH.length) || '/'
    : rawPath;
  const normalized = path.startsWith('/') ? path : `/${path}`;

  if (normalized !== BLYN_HOST_BASE && !normalized.startsWith(`${BLYN_HOST_BASE}/`)) {
    return undefined;
  }

  const rest = normalized.slice(BLYN_HOST_BASE.length).replace(/^\//, '');
  if (!rest || rest === '_') return undefined;

  return `/blyn/${rest}${window.location.search}`;
}

/** `/blyn/projects?id=1` → `/ai-agent/projects?id=1` (no `/v2` prefix). */
export function blynRouteToHostPath(blynRoute: string): string {
  const [pathname = '', search = ''] = blynRoute.split('?');
  const view = pathname.replace(/^\/blyn\/?/, '');
  const query = search ? `?${search}` : '';
  if (!view) return BLYN_HOST_BASE;
  return `${BLYN_HOST_BASE}/${view}${query}`;
}

/** Full browser path including `/v2`, used with `history.replaceState`. */
export function blynRouteToBrowserHref(blynRoute: string): string {
  const [path, search = ''] = blynRouteToHostPath(blynRoute).split('?');
  const href = pathWithAppBase(path);
  return search ? `${href}?${search}` : href;
}

/** Public share URL for a conversation or project (includes origin + `/v2`). */
export function hostShareHref(kind: 'conversation' | 'project', id: string): string {
  const view = kind === 'conversation' ? 'new-conversation' : 'project-workspace';
  return `${getSelfUrlOrigin()}${pathWithAppBase(`/ai-agent/${view}`)}?id=${encodeURIComponent(id)}`;
}

export function syncBlynRouteToBrowser(blynRoute: string) {
  if (typeof window === 'undefined') return;
  if (!blynRoute.startsWith('/blyn')) return;

  const next = blynRouteToBrowserHref(blynRoute);
  const current = `${window.location.pathname}${window.location.search}`;
  if (current === next) return;

  window.history.replaceState(window.history.state, '', next);
}
